import {
  Button,
  Modal,
  SideModal as SideModalComponent
} from '@citygross/components';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import SideModalHeader from './SideModalHeader';
import { theme } from '@citygross/design-tokens';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeSideModal as closeSideModalGlobal,
  selectShowModal,
  selectModalTab,
  selectTabHistory,
  selectModalForceOpen,
  sideModalForceOpen,
  openSideModal,
  selectPreviouslyFocusedElementId
} from '../../slices/sideModalSlice';
import { SideModalLogin } from '../SideModalTabs/SideModalLogin';
import SideModalMenu from '../SideModalTabs/SideModalMenu/SideModalMenu';
import { SideModalStoreSelector } from '../SideModalTabs/SideModalStoreSelector';
import ShoppingOrder from '../../containers/ShoppingOrderSideModal';
import * as styles from './SideModal.styles';
import { TSideModalTabs } from '../../types/sideModalTypes';
import { AnimatePresence } from 'framer-motion/dist/framer-motion';
import { BasicAnimatedStep } from '../SideModalTabs/AnimatedStep';
import { SideModalRegister } from '../SideModalTabs/SideModalRegister';
import {
  selectRegisterCurrentStep,
  setCurrentRegisterStep
} from '../../slices/loginSlice';
import { focusTrap, useDetectDevice, useWindowSize } from '@citygross/utils';
import { getAssortmentCookie } from '../../lib/assortments';

export const SideModalTabs: TSideModalTabs = {
  CART: 'cart',
  LOGIN: 'login',
  MENU: 'menu',
  REGISTER: 'register',
  STORE_SELECTOR: 'storeSelector'
};

export const SideModal = ({ confirmModalRef }: any) => {
  const dispatch = useDispatch();
  const [initialScroll, setInitialScroll] = useState(true);
  const { width } = useWindowSize();
  const { isMobile } = useDetectDevice();
  const [visualHeight, setVisualHeight] = useState<string>('100%');
  const assortmentCookie = getAssortmentCookie();

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const sideModalRef = useRef<HTMLDivElement | null>(null);
  const registerModalRef = useRef<HTMLDivElement | null>(null);

  const showSideModal = useSelector(selectShowModal);
  const modalTab = useSelector(selectModalTab);
  const forceOpen = useSelector(selectModalForceOpen);
  const currentRegisterStep = useSelector(selectRegisterCurrentStep);
  const tabHistory = useSelector(selectTabHistory);
  const previouslyFocusedElementId = useSelector(
    selectPreviouslyFocusedElementId
  );
  const [prevPosition, setPrevPosition] = useState(0);

  const isInitial = tabHistory.length < 2;

  const closeSideModal = () => {
    const element = document.getElementById(previouslyFocusedElementId);
    if (element) element.focus();

    dispatch(closeSideModalGlobal());
    setInitialScroll(true);
  };

  const checkVisualViewPort = () => {
    if (width > 991 || !isMobile) {
      setVisualHeight('100dvh');
    } else {
      setVisualHeight(window.visualViewport?.height + 'px');
    }
  };

  const closeModalClick = () => {
    forceOpen
      ? () => {}
      : modalTab === SideModalTabs.REGISTER && currentRegisterStep > 0
      ? setShowRegisterModal(true)
      : closeSideModal();
  };

  const closeRegisterModal = () => setShowRegisterModal(false);

  useEffect(() => {
    if (assortmentCookie?.deliveryType === 'homeDelivery') {
      dispatch(openSideModal(SideModalTabs.STORE_SELECTOR));
      dispatch(sideModalForceOpen(true));
    }
  }, [assortmentCookie?.deliveryType]);

  useEffect(() => {
    const pos = window?.scrollY ?? 0;
    const imboxContainer = document.getElementById('imbox-container');
    const isModalOpen = showSideModal || showRegisterModal;

    let trap: (() => void) | null = null;

    if (isModalOpen) {
      if (showRegisterModal) {
        trap = focusTrap({
          exitFunction: closeRegisterModal,
          ref: registerModalRef
        });
      } else if (showSideModal) {
        trap = focusTrap({
          exitFunction: closeSideModal,
          ref: sideModalRef
        });
      }

      setPrevPosition(pos);
      document.body.style.overflowY = 'scroll';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${pos}px`;

      if (imboxContainer) {
        imboxContainer.style.display = 'none';
      }
    } else {
      if (prevPosition) {
        window.scrollTo(0, prevPosition);
      }

      document.body.style.removeProperty('top');
      document.body.style.overflowY = 'auto';
      document.body.style.position = 'relative';

      if (imboxContainer) {
        imboxContainer.style.display = 'block';
      }
    }

    checkVisualViewPort();
    visualViewport?.addEventListener('resize', checkVisualViewPort);

    return () => {
      visualViewport?.removeEventListener('resize', checkVisualViewPort);
      trap?.();
    };
  }, [showSideModal, showRegisterModal]);

  return (
    <Fragment>
      <SideModalComponent
        background={theme.palette?.white}
        isVisible={showSideModal}
        maxWidth={
          modalTab === SideModalTabs.CART && width && width < 480
            ? '100%'
            : '80%'
        }
        onBackdropClick={closeModalClick}
        ref={sideModalRef}
        width={375}
      >
        <AnimatePresence initial={!isInitial}>
          {(() => {
            switch (modalTab) {
              case SideModalTabs.LOGIN:
                return (
                  <styles.SideModalLoginSlideContainer>
                    <SideModalLogin closeModal={closeSideModal} />
                  </styles.SideModalLoginSlideContainer>
                );
              case SideModalTabs.REGISTER:
                return (
                  <styles.SideModalLoginSlideContainer
                    clientHeight={visualHeight}
                  >
                    <SideModalRegister closeModal={closeModalClick} />
                  </styles.SideModalLoginSlideContainer>
                );
              case SideModalTabs.MENU:
                return (
                  <styles.SideModalMenuSlideContainer
                    clientHeight={visualHeight}
                  >
                    <SideModalMenu
                      closeModal={closeSideModal}
                      initialScroll={initialScroll}
                      setInitialScroll={setInitialScroll}
                    />
                  </styles.SideModalMenuSlideContainer>
                );
              case SideModalTabs.STORE_SELECTOR:
                return (
                  <BasicAnimatedStep
                    key={SideModalTabs.STORE_SELECTOR}
                    slideInDirection={-1}
                  >
                    <styles.SideModalStoreSelectorSlideContainer
                      clientHeight={visualHeight}
                    >
                      <SideModalStoreSelector
                        clientHeight={visualHeight}
                        closeModal={closeModalClick}
                      />
                    </styles.SideModalStoreSelectorSlideContainer>
                  </BasicAnimatedStep>
                );
              case SideModalTabs.CART:
                return (
                  <BasicAnimatedStep
                    slideInDirection={1}
                    key={SideModalTabs.CART}
                  >
                    <styles.SideModalCartSlideContainer>
                      <SideModalHeader
                        closeSideModal={closeSideModal}
                        isCart
                        marginBottom={0}
                        title="Varukorg"
                      />
                      <styles.CartContainer visualHeight={visualHeight}>
                        <ShoppingOrder confirmModalRef={confirmModalRef} />
                      </styles.CartContainer>
                    </styles.SideModalCartSlideContainer>
                  </BasicAnimatedStep>
                );
              default:
                return null;
            }
          })()}
        </AnimatePresence>
      </SideModalComponent>

      {showRegisterModal && (
        <Modal
          footer={
            <Fragment>
              <Button color="white" fullWidth onClick={closeRegisterModal}>
                Avbryt
              </Button>

              <Button
                color="secondary"
                fullWidth
                onClick={() => {
                  dispatch(
                    setCurrentRegisterStep({
                      currentStep: 0,
                      previousStep: 0
                    })
                  );
                  closeRegisterModal();
                  closeSideModal();
                }}
              >
                Ja, avslut
              </Button>
            </Fragment>
          }
          isConfirm
          onBackdropClick={() => {
            setShowRegisterModal(false);
          }}
          ref={registerModalRef}
          subTitle="Om du stänger nu måste du börja om från början."
          title="Avsluta registrering"
        />
      )}
    </Fragment>
  );
};
